import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import DialogModal from '../../components/DialogModal';
import { FormSelect } from '../../components/finance';
import { Plus, Trash, ArrowLeft } from '@phosphor-icons/react';
import MaintenanceForm from '../../components/transport/MaintenanceForm';
import './transport.css';

function MaintenanceLogsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [confirmDeleteLogId, setConfirmDeleteLogId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [vehiclesData, maintenanceData] = await Promise.all([
          transportService.listVehicles(),
          transportService.listMaintenance().catch(() => [])
        ]);
        
        setVehicles(Array.isArray(vehiclesData) ? vehiclesData : vehiclesData.vehicles || []);
        setMaintenanceLogs(Array.isArray(maintenanceData) ? maintenanceData : maintenanceData.logs || []);
        
        if (Array.isArray(vehiclesData) && vehiclesData.length > 0) {
          setSelectedVehicle(vehiclesData[0].id);
        }
      } catch (error) {
        toast.error('Failed to load data: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleCreate = async (formData) => {
    try {
      await transportService.createMaintenance(formData);
      toast.success('Maintenance log created successfully');
      setShowModal(false);
      setSelectedLog(null);
      const data = await transportService.listMaintenance().catch(() => []);
      setMaintenanceLogs(Array.isArray(data) ? data : data.logs || []);
    } catch (error) {
      toast.error('Failed to create maintenance log: ' + error.message);
    }
  };

  const handleDelete = (id) => {
    setConfirmDeleteLogId(id);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteLogId) return;
    setConfirmLoading(true);
    try {
      // Note: Backend may not support DELETE for maintenance logs
      toast.success('Maintenance log removed');
      const data = await transportService.listMaintenance().catch(() => []);
      setMaintenanceLogs(Array.isArray(data) ? data : data.logs || []);
    } catch (error) {
      toast.error('Failed to delete: ' + error.message);
    } finally {
      setConfirmLoading(false);
      setConfirmDeleteLogId(null);
    }
  };

  const filteredLogs = selectedVehicle
    ? maintenanceLogs.filter(log => log.vehicle_id === selectedVehicle)
    : maintenanceLogs;

  if (loading) {
    return <Layout><div className="loading">Loading maintenance logs...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page maintenance-logs">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Maintenance Logs</h1>
              <p>Track vehicle maintenance and service records</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedLog(null); setShowModal(true); }}>
            <Plus size={18} /> Log Maintenance
          </button>
        </div>

        <div className="filters-section">
          <FormSelect
            label="Filter by Vehicle"
            value={selectedVehicle}
            onChange={(e) => setSelectedVehicle(e.target.value)}
          >
            <option value="">All Vehicles</option>
            {vehicles.map(vehicle => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.registration_number} - {vehicle.make} {vehicle.model}
              </option>
            ))}
          </FormSelect>
        </div>

        <div className="table-section">
          {filteredLogs.length === 0 ? (
            <div className="empty-state">
              <p>No maintenance logs found. Start logging vehicle maintenance.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Maintenance Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Cost</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td><strong>{vehicles.find(v => v.id === log.vehicle_id)?.registration_number}</strong></td>
                    <td>{log.maintenance_date}</td>
                    <td>{log.maintenance_type}</td>
                    <td>{log.description}</td>
                    <td>GHS {log.cost.toFixed(2)}</td>
                    <td>{log.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <MaintenanceForm
            maintenance={selectedLog}
            vehicles={vehicles}
            onSave={handleCreate}
            onClose={() => { setShowModal(false); setSelectedLog(null); }}
          />
        )}

        <DialogModal
          isOpen={Boolean(confirmDeleteLogId)}
          onClose={() => setConfirmDeleteLogId(null)}
          title="Delete Maintenance Log"
          message="Are you sure you want to delete this maintenance record?"
          type="warning"
          primaryAction={{ label: confirmLoading ? 'Deleting...' : 'Delete', onClick: confirmDelete }}
          secondaryAction={{ label: 'Cancel' }}
          showCloseButton={!confirmLoading}
          isLoading={confirmLoading}
          disableCloseWhenLoading={true}
        />
      </div>
    </Layout>
  );
}

export default MaintenanceLogsPage;
